import { useEffect, useMemo, useState } from 'react'
import { theme } from '../theme'
import { Ic, RecipeThumb } from '../icons'
import { DOW_LONG, SLOT_LABELS } from '../dates'
import { api } from '../api'
import { ConfirmDialog } from './Dialog'
import { ImageLightbox } from './ImageLightbox'
import { Stars } from './RecipesTab'
import { MealAdjustModal } from './MealAdjustModal'
import type { Recipe, RecipeDetail, Meal, MealDetail, MealStatus, RecipeIngredient } from '../types'

type Props = {
  recipeId: number
  // Optional cached summary so the hero renders instantly while the full
  // detail (ingredients/instructions) loads.
  recipe?: Recipe | null
  meal?: Meal
  mobile: boolean
  onBack: () => void
  onChange?: () => void
  onRemove?: () => void
  onSetStatus?: (status: MealStatus) => void
  onAdopt?: () => void
  onEdit?: () => void
  onDelete?: () => void
  onViewOriginal?: (originalId: number) => void
  alreadyAdopted?: boolean
}

export function RecipeViewPage({
  recipeId, recipe, meal, mobile, onBack, onChange, onRemove, onSetStatus,
  onAdopt, onEdit, onDelete, onViewOriginal, alreadyAdopted,
}: Props) {
  const [detail, setDetail] = useState<RecipeDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [confirmDel, setConfirmDel] = useState(false)
  const [mealDetail, setMealDetail] = useState<MealDetail | null>(null)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [scaleBusy, setScaleBusy] = useState(false)
  const [zoomPhoto, setZoomPhoto] = useState(false)

  useEffect(() => {
    let cancelled = false
    setDetail(null)
    setLoading(true)
    api.recipe(recipeId)
      .then(d => { if (!cancelled) setDetail(d) })
      .catch(e => console.error(e))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [recipeId])

  // In meal mode, load the meal's *effective* ingredient list (override if
  // adjusted, else the recipe's). Re-fetch whenever the meal's adjustment
  // signature changes — including live patches landing from a save elsewhere.
  const mealId = meal?.id
  useEffect(() => {
    if (mealId == null) { setMealDetail(null); return }
    let cancelled = false
    api.meal(mealId)
      .then(d => { if (!cancelled) setMealDetail(d) })
      .catch(e => console.error(e))
    return () => { cancelled = true }
  }, [mealId, meal?.adjusted, meal?.servings, meal?.recipe_id])

  // Hero fields come from whichever we have first: the cached summary or the
  // freshly loaded detail.
  const head = detail ?? recipe
  const isMealMode = !!meal
  const recipeIngredients = detail?.ingredients ?? []
  const instructions = detail?.instructions ?? ''

  // The list to render: in meal mode it's the effective list; otherwise the
  // recipe's. Each row is annotated against the recipe so the page can show
  // what changed for this instance.
  const ingredientRows = useMemo<IngredientDiffRow[]>(() => {
    if (!isMealMode) return recipeIngredients.map(i => ({ ...i, diff: 'same' as const }))
    if (!mealDetail) return recipeIngredients.map(i => ({ ...i, diff: 'same' as const }))
    if (!meal?.adjusted) return mealDetail.ingredients.map(i => ({ ...i, diff: 'same' as const }))
    return diffIngredients(recipeIngredients, mealDetail.ingredients)
  }, [isMealMode, mealDetail, meal?.adjusted, recipeIngredients])

  // Serving count shown on the meal page: the adjusted count if set, else the
  // recipe's. Scaling needs a positive basis.
  const mealServings = meal?.servings ?? detail?.servings ?? 0

  const onScaleServings = async (next: number) => {
    if (!meal || !mealDetail || next <= 0 || mealServings <= 0 || next === mealServings) return
    const factor = next / mealServings
    const scaled: RecipeIngredient[] = mealDetail.ingredients.map(i => ({
      ...i, quantity: Math.round(i.quantity * factor * 100) / 100,
    }))
    setScaleBusy(true)
    try {
      const d = await api.setMealAdjustments(meal.id, {
        servings: detail && next === detail.servings ? null : next,
        note: mealDetail.notes ?? null,
        ingredients: scaled,
      })
      setMealDetail(d)
    } catch (e) {
      console.error(e)
    } finally {
      setScaleBusy(false)
    }
  }

  let dateLabel = ''
  if (meal) {
    const d = new Date(meal.date + 'T00:00:00')
    dateLabel = `${DOW_LONG[(d.getDay() + 6) % 7]}, ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
  }

  const tagList = head
    ? Object.entries(head.tags).flatMap(([k, vs]) => vs.map(v => ({ k, v })))
    : []

  const showFooter =
    isMealMode ||
    (detail && detail.mine) ||
    (detail && !detail.mine && onAdopt && !alreadyAdopted)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 720 }}>
      <button onClick={onBack} style={{
        all: 'unset', cursor: 'pointer', alignSelf: 'flex-start',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
        textTransform: 'uppercase', color: theme.inkSoft,
        padding: '4px 8px 4px 4px', borderRadius: theme.radius,
      }}>
        <Ic.chevL width="16" height="16"/> {isMealMode ? 'Back to plan' : 'Back to recipes'}
      </button>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', minWidth: 0 }}>
        {head?.image_url ? (
          <button
            onClick={() => setZoomPhoto(true)}
            title="View full photo"
            style={{
              all: 'unset', cursor: 'zoom-in', display: 'inline-flex',
              flex: '0 0 auto', borderRadius: theme.radius,
            }}
          >
            <RecipeThumb imageUrl={head.image_url} hue={head?.hue ?? 0} name={head?.name ?? '?'} size={64} radius={theme.radius}/>
          </button>
        ) : (
          <RecipeThumb imageUrl={head?.image_url} hue={head?.hue ?? 0} name={head?.name ?? '?'} size={64} radius={theme.radius}/>
        )}
        <div style={{ minWidth: 0 }}>
          {isMealMode && (
            <div style={{
              fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
              textTransform: 'uppercase', color: theme.muted,
            }}>{SLOT_LABELS[meal!.slot]} · {dateLabel}</div>
          )}
          <h1 style={{
            fontFamily: theme.serif, fontSize: mobile ? 28 : 34, fontWeight: theme.headWeight,
            color: theme.ink, margin: isMealMode ? '2px 0 0' : 0, lineHeight: 1.1,
          }}>{head?.name ?? 'Loading…'}</h1>
          {head && (
            <div style={{
              fontFamily: theme.sans, fontSize: 12, color: theme.muted,
              marginTop: 8, display: 'flex', gap: 10, alignItems: 'center',
              textTransform: 'uppercase', letterSpacing: 0.6, flexWrap: 'wrap',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Ic.clock width="12" height="12"/> {head.prep_minutes}m
              </span>
              {!isMealMode && <>
                <span style={{ opacity: 0.5 }}>·</span>
                <span>{(head.yield_unit && head.yield_unit !== 'serving')
                  ? `makes ${formatQty(head.yield_quantity, head.yield_unit)}`
                  : `${head.servings} servings`}</span>
              </>}
              {!isMealMode && head.tags?.used_as && !head.tags.used_as.includes('meal') && (
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: 1,
                  color: theme.accent, border: `1px solid ${theme.accent}`,
                  borderRadius: 4, padding: '1px 5px',
                }}>ingredient</span>
              )}
              {detail && !detail.mine && detail.author_name && <>
                <span style={{ opacity: 0.5 }}>·</span>
                <span style={{ fontStyle: 'italic', textTransform: 'none' }}>by {detail.author_name}</span>
              </>}
            </div>
          )}
          {detail && (detail.rating_count > 0 || (!detail.mine && detail.published)) && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6,
              fontFamily: theme.sans, fontSize: 12, color: theme.muted,
            }}>
              <Stars value={detail.avg_rating ?? 0} size={14}/>
              <span>{detail.rating_count > 0
                ? `${detail.avg_rating?.toFixed(1)} (${detail.rating_count})`
                : 'unrated'}</span>
            </div>
          )}
        </div>
      </div>

      {isMealMode && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          padding: '12px 14px', background: theme.surface,
          border: theme.cardBorder, borderRadius: theme.radiusLg,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 700,
              textTransform: 'uppercase', color: theme.muted,
            }}>Servings</span>
            <HeroStepper
              value={mealServings}
              disabled={mealServings <= 0 || scaleBusy || !mealDetail || meal!.status === 'cooked'}
              onChange={onScaleServings}
            />
          </div>
          {meal!.adjusted && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
                textTransform: 'uppercase', color: theme.accentInk,
              }}>Adjusted for this meal</span>
              {meal!.status !== 'cooked' && (
                <button onClick={async () => {
                  try { const d = await api.revertMealAdjustments(meal!.id); setMealDetail(d) }
                  catch (e) { console.error(e) }
                }} style={{
                  all: 'unset', cursor: 'pointer', fontFamily: theme.sans, fontSize: 11,
                  color: theme.muted, textDecoration: 'underline',
                }}>Revert to recipe</button>
              )}
            </div>
          )}
        </div>
      )}

      {isMealMode && mealDetail?.notes && (
        <div style={{
          fontFamily: theme.serif, fontSize: 14, fontStyle: 'italic', color: theme.inkSoft,
          lineHeight: 1.5, padding: '2px 2px',
        }}>“{mealDetail.notes}”</div>
      )}

      {tagList.length > 0 && (
        <Section label="Tags">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {tagList.map(({ k, v }, i) => (
              <span key={i} style={{
                fontFamily: theme.sans, fontSize: 11, color: theme.inkSoft,
                background: theme.bg, border: `1px solid ${theme.line}`,
                padding: '3px 9px', borderRadius: 999,
              }}>
                <span style={{ color: theme.muted, marginRight: 4 }}>{k}:</span>{v}
              </span>
            ))}
          </div>
        </Section>
      )}

      <Section label="Ingredients">
        {loading && !detail ? (
          <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted }}>Loading…</div>
        ) : ingredientRows.length === 0 ? (
          <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted, fontStyle: 'italic' }}>
            No ingredients recorded yet.
          </div>
        ) : (
          <ul style={{
            margin: 0, padding: 0, listStyle: 'none',
            display: 'flex', flexDirection: 'column', gap: 4,
            fontFamily: theme.sans, fontSize: 13, color: theme.ink,
          }}>
            {ingredientRows.map((i, idx) => {
              const removed = i.diff === 'removed'
              return (
                <li key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', gap: 12,
                  padding: '5px 0', borderBottom: `1px dotted ${theme.line}`,
                  opacity: removed ? 0.5 : i.optional ? 0.7 : 1,
                  textDecoration: removed ? 'line-through' : 'none',
                }}>
                  <span>
                    {i.diff === 'added' && <span style={{ color: theme.accentInk, fontWeight: 700, marginRight: 4 }}>+</span>}
                    {i.name}
                    {i.prep && <span style={{ color: theme.muted, marginLeft: 6 }}>· {i.prep}</span>}
                    {i.optional && <span style={{ color: theme.muted, fontStyle: 'italic', marginLeft: 6 }}>(optional)</span>}
                    {i.diff === 'added' && <span style={{ color: theme.accentInk, fontStyle: 'italic', marginLeft: 6, fontSize: 11 }}>added</span>}
                  </span>
                  <span style={{ color: theme.inkSoft, whiteSpace: 'nowrap' }}>
                    {removed ? (
                      <span style={{ fontStyle: 'italic' }}>skipped</span>
                    ) : i.diff === 'changed' && i.fromQty != null ? (
                      <>
                        <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: 6 }}>
                          {formatQty(i.fromQty, i.fromUnit ?? '')}
                        </span>
                        {formatQty(i.quantity, i.unit)}
                      </>
                    ) : (
                      formatQty(i.quantity, i.unit)
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </Section>

      <Section label="Instructions">
        {loading && !detail ? (
          <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted }}>Loading…</div>
        ) : instructions ? (
          <div style={{
            fontFamily: theme.serif, fontSize: 15, color: theme.ink,
            lineHeight: 1.6, whiteSpace: 'pre-wrap',
          }}>{instructions}</div>
        ) : (
          <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted, fontStyle: 'italic' }}>
            No instructions recorded yet.
          </div>
        )}
      </Section>

      {detail && !detail.mine && detail.published && (
        <Section label="Your rating">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Stars value={detail.my_rating ?? 0} size={24} interactive
              onPick={async (stars) => {
                try {
                  await api.rateRecipe(detail.id, stars)
                  setDetail({ ...detail, my_rating: stars })
                } catch (e) { console.error(e) }
              }}
            />
            {detail.my_rating ? (
              <button onClick={async () => {
                try {
                  await api.unrateRecipe(detail.id)
                  setDetail({ ...detail, my_rating: 0 })
                } catch (e) { console.error(e) }
              }} style={{
                all: 'unset', cursor: 'pointer',
                fontFamily: theme.sans, fontSize: 11, color: theme.muted,
                textDecoration: 'underline',
              }}>Clear</button>
            ) : (
              <span style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted, fontStyle: 'italic' }}>
                Tap a star to rate
              </span>
            )}
          </div>
        </Section>
      )}

      {detail && detail.mine && (
        <Section label="Publishing">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={async () => {
              try {
                await api.publishRecipe(detail.id, !detail.published)
                setDetail({ ...detail, published: !detail.published })
              } catch (e) { console.error(e) }
            }} style={{
              all: 'unset', cursor: 'pointer',
              padding: '8px 14px', borderRadius: theme.radius,
              fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: detail.published ? theme.ink : theme.surface,
              background: detail.published ? 'transparent' : theme.ink,
              border: `1px solid ${detail.published ? theme.lineStrong : theme.ink}`,
            }}>{detail.published ? 'Unpublish' : 'Publish to catalog'}</button>
            <span style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted }}>
              {detail.published
                ? 'Anyone signed in can find and rate this recipe.'
                : 'Only you can see this recipe right now.'}
            </span>
          </div>
        </Section>
      )}

      {isMealMode && onSetStatus && (
        <Section label="Status">
          <div style={{ display: 'flex', gap: 6 }}>
            {(['planned', 'cooked', 'skipped'] as MealStatus[]).map(s => {
              const active = meal!.status === s
              return (
                <button key={s} onClick={() => onSetStatus(s)} style={{
                  all: 'unset', cursor: 'pointer',
                  padding: '6px 12px', borderRadius: 999,
                  fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: active ? theme.surface : theme.inkSoft,
                  background: active ? theme.ink : 'transparent',
                  border: `1px solid ${active ? theme.ink : theme.lineStrong}`,
                }}>{s}</button>
              )
            })}
          </div>
        </Section>
      )}

      {showFooter && (
        <div style={{
          borderTop: `1px solid ${theme.line}`, paddingTop: 16, marginTop: 2,
          display: 'flex', justifyContent: isMealMode ? 'space-between' : 'flex-start',
          alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          {isMealMode ? (
            <button onClick={onRemove} style={{
              all: 'unset', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: theme.radius,
              fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
              textTransform: 'uppercase', color: theme.accentInk,
            }}>
              <Ic.trash width="14" height="14"/> Remove from plan
            </button>
          ) : <span/>}
          <div style={{ display: 'flex', flexDirection: isMealMode ? 'row' : 'row-reverse', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {detail && detail.mine && detail.forked_from && onViewOriginal && (
              <button onClick={() => onViewOriginal(detail.forked_from!)} style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 14px', borderRadius: theme.radius,
                fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
                textTransform: 'uppercase', color: theme.inkSoft,
                border: `1px solid ${theme.lineStrong}`,
              }}>View original</button>
            )}
            {detail && detail.mine && onEdit && (
              <button onClick={onEdit} style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 14px', borderRadius: theme.radius,
                fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
                textTransform: 'uppercase', color: theme.ink,
                border: `1px solid ${theme.lineStrong}`,
              }}>Edit</button>
            )}
            {!isMealMode && detail && detail.mine && onDelete && (
              <button
                onClick={() => { if (!detail.published) setConfirmDel(true) }}
                disabled={detail.published}
                title={detail.published ? 'Unpublish before deleting' : undefined}
                style={{
                  all: 'unset',
                  cursor: detail.published ? 'not-allowed' : 'pointer',
                  padding: '8px 14px', borderRadius: theme.radius,
                  fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: theme.surface,
                  background: 'oklch(0.42 0.16 28)',
                  opacity: detail.published ? 0.4 : 1,
                }}
              >Delete</button>
            )}
            {isMealMode && meal!.status !== 'cooked' && (
              <button onClick={() => setAdjustOpen(true)} disabled={!mealDetail} style={{
                all: 'unset', cursor: mealDetail ? 'pointer' : 'default',
                padding: '8px 14px', borderRadius: theme.radius,
                fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
                textTransform: 'uppercase', color: theme.ink,
                border: `1px solid ${theme.lineStrong}`, opacity: mealDetail ? 1 : 0.5,
              }}>Adjust this meal</button>
            )}
            {isMealMode && (
              <button onClick={onChange} style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 14px', borderRadius: theme.radius,
                fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase', color: theme.surface, background: theme.ink,
              }}>Change recipe</button>
            )}
            {!isMealMode && detail && !detail.mine && onAdopt && !alreadyAdopted && (
              <button onClick={onAdopt} style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 14px', borderRadius: theme.radius,
                fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase', color: theme.surface, background: theme.ink,
              }}>Adopt to my cookbook</button>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDel}
        title="Delete recipe"
        message={`Delete "${head?.name ?? 'this recipe'}"? Any planned meals using it will be cleared. This can't be undone.`}
        confirmLabel="Delete"
        danger
        onCancel={() => setConfirmDel(false)}
        onConfirm={() => { setConfirmDel(false); onDelete?.() }}
      />

      {head?.image_url && (
        <ImageLightbox
          open={zoomPhoto}
          src={head.image_url}
          alt={head.name ?? ''}
          onClose={() => setZoomPhoto(false)}
        />
      )}

      {isMealMode && meal && mealDetail && (
        <MealAdjustModal
          open={adjustOpen}
          mealId={meal.id}
          initial={{ servings: meal.servings, note: mealDetail.notes, ingredients: mealDetail.ingredients }}
          recipeServings={detail?.servings ?? 0}
          adjusted={!!meal.adjusted}
          mobile={mobile}
          onClose={() => setAdjustOpen(false)}
          onSaved={(d) => { setMealDetail(d); setAdjustOpen(false) }}
        />
      )}
    </div>
  )
}

function formatQty(q: number, unit: string): string {
  if (!q) return unit || ''
  const n = Number.isInteger(q) ? q.toString() : q.toFixed(2).replace(/\.?0+$/, '')
  return unit ? `${n} ${unit}` : n
}

// IngredientDiffRow is a display ingredient annotated with how it differs from
// the recipe: 'added' (one-off), 'removed' (skipped), 'changed' (qty/unit), or
// 'same'. `fromQty`/`fromUnit` carry the recipe's original for a changed row.
type IngredientDiffRow = RecipeIngredient & {
  diff: 'same' | 'added' | 'removed' | 'changed'
  fromQty?: number
  fromUnit?: string
}

// diffIngredients renders the union of the recipe's list and the meal's
// effective list: recipe rows missing from the meal show as 'removed', meal
// rows missing from the recipe as 'added', and quantity/unit changes as
// 'changed'. Keyed by ingredient_id.
function diffIngredients(recipe: RecipeIngredient[], effective: RecipeIngredient[]): IngredientDiffRow[] {
  const eff = new Map(effective.map(i => [i.ingredient_id, i]))
  const rec = new Map(recipe.map(i => [i.ingredient_id, i]))
  const out: IngredientDiffRow[] = []
  for (const r of recipe) {
    const e = eff.get(r.ingredient_id)
    if (!e) { out.push({ ...r, diff: 'removed' }); continue }
    if (e.quantity !== r.quantity || e.unit !== r.unit) {
      out.push({ ...e, diff: 'changed', fromQty: r.quantity, fromUnit: r.unit })
    } else {
      out.push({ ...e, diff: 'same' })
    }
  }
  for (const e of effective) {
    if (!rec.has(e.ingredient_id)) out.push({ ...e, diff: 'added' })
  }
  return out
}

function HeroStepper({ value, disabled, onChange }: { value: number; disabled?: boolean; onChange: (n: number) => void }) {
  const btn: React.CSSProperties = {
    all: 'unset', cursor: disabled ? 'default' : 'pointer',
    width: 28, height: 28, display: 'grid', placeItems: 'center',
    border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
    color: theme.ink, fontFamily: theme.sans, fontSize: 16, fontWeight: 600,
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: disabled ? 0.5 : 1 }}>
      <button style={btn} disabled={disabled} onClick={() => onChange(Math.max(1, value - 1))}>−</button>
      <span style={{ fontFamily: theme.serif, fontSize: 17, fontWeight: theme.headWeight, color: theme.ink, minWidth: 18, textAlign: 'center' }}>
        {value || '—'}
      </span>
      <button style={btn} disabled={disabled} onClick={() => onChange(value + 1)}>+</button>
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: theme.surface, border: theme.cardBorder, borderRadius: theme.radiusLg,
      padding: '14px 16px',
    }}>
      <div style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 700,
        textTransform: 'uppercase', color: theme.muted, marginBottom: 8,
      }}>{label}</div>
      {children}
    </div>
  )
}
